import React, {useEffect, useState} from "react";
import {BillingInterface, BillingItemInterface} from "../../../service/Business/Interfaces/BillingInterface";
import StatusBilling from "../../../utils/Business/StatusBilling";
import FCDatePicker from "../../FCObjets/Form/FCDatePicker";
import {getIdWithToken} from "../../../utils/storage/Token";
import {useFetchOpenBusinessDealContent} from "../../../service/Business/BusinessService";
import FCSpinner from "../../FCObjets/FCSpinner";
import {AddBilling, UpdateBilling} from "../../../service/Business/BillingService";
import {CallbackErrorModalFL, CallbackFinishedModalFL} from "../../Dashboard/Form/ModalForm";
import FCAlertMsgErrorAPI from "../../FCObjets/FCAlertMsg/FCAlertMsgErrorAPI";
import TableBillingItem from "./Tables/TableBillingItem";
import {AddBillingItem, DeleteBillingItem, UpdateBillingItem} from "../../../service/Business/BillingItemService";
import FCSelect, {FCSelectOption} from "../../FCObjets/Form/FCSelect";
import {useToast} from "../../FCObjets/FCToast/FCToast";
import "./FormAtelier.scss";

type FormBillingProps = {
    onFinished: CallbackFinishedModalFL;
    onError: CallbackErrorModalFL;
    dataBilling: BillingInterface | undefined;
};

const FormBilling: React.FunctionComponent<FormBillingProps> = ({dataBilling, onFinished, onError}) => {
    const toast = useToast();

    const [inputBusiness, setInputBusiness] = useState<number>(0);
    const [inputNumber, setInputNumber] = useState(dataBilling ? dataBilling.number : "");
    const [inputState, setInputState] = useState(dataBilling ? dataBilling.state : 0);
    const [dateBegin, setDateBegin] = useState<Date | null>(dataBilling ? new Date(dataBilling.begin) : null);
    const [dateEnd, setDateEnd] = useState<Date | null>(dataBilling ? new Date(dataBilling.end) : null);

    const [optionsBusiness, setOptionsBusiness] = useState<FCSelectOption[]>([]);

    const [listBillingItemAdd, setListBillingItemAdd] = useState<BillingItemInterface[]>([]);
    const [listBillingItemDel, setListBillingItemDel] = useState<BillingItemInterface[]>([]);
    const [listBillingItemModify, setListBillingItemModify] = useState<BillingItemInterface[]>([]);

    const {data: businessContent, loading, error} = useFetchOpenBusinessDealContent(getIdWithToken());
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (businessContent) {
            const option: FCSelectOption[] = [];
            for (let i = 0; i < businessContent.length; i++) {
                option.push({value: i.toString(), label: businessContent[i].business.name});
                if (businessContent[i].business.id === dataBilling?.businessDeal.id) {
                    setInputBusiness(i);
                }
            }
            setOptionsBusiness(option);
        }
    }, [businessContent, dataBilling]);

    const handlerEventContact = (listAdd: BillingItemInterface[], listDel: BillingItemInterface[], listModify: BillingItemInterface[]) => {
        setListBillingItemAdd(listAdd);
        setListBillingItemDel(listDel);
        setListBillingItemModify(listModify);
    };

    /**
     * Validation du formulaire
     */
    const handleSubmit = async () => {
        const errors: Record<string, string> = {};

        if (!inputNumber.trim()) errors.inputNumber = "Le numéro de facture est obligatoire.";
        if (!dateBegin) errors.dateBegin = "La date de début est obligatoire.";
        if (!dateEnd) errors.dateEnd = "La date de fin est obligatoire.";
        if (dateBegin && dateEnd && dateEnd < dateBegin) errors.dateEnd = "La date de fin doit être postérieure à la date de début.";
        if (!businessContent || businessContent.length === 0) errors.inputBusiness = "Aucun contrat disponible.";

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            toast.error(Object.values(errors).join(" "), "Validation");
            return;
        }
        setFieldErrors({});

        let business = dataBilling?.businessDeal;
        if (businessContent) {
            business = businessContent[inputBusiness].business;
        }

        const billing: any = {
            begin: dateBegin?.toDateString(),
            end: dateEnd?.toDateString(),
            businessDeal: business,
            billingItems: [],
            id: dataBilling ? dataBilling.id : -1,
            number: inputNumber,
            state: inputState
        };

        try {
            const itemPromises: Promise<any>[] = [];
            if (listBillingItemAdd.length !== 0) {
                itemPromises.push(AddBillingItem(dataBilling ? dataBilling.id : 0, {
                    listBillingItem: listBillingItemAdd
                }));
            }
            if (listBillingItemDel.length !== 0) {
                for (let i = 0; i < listBillingItemDel.length; i++) {
                    itemPromises.push(DeleteBillingItem(listBillingItemDel[i]));
                }
            }
            if (listBillingItemModify.length !== 0) {
                itemPromises.push(UpdateBillingItem(getIdWithToken(), {listBillingItem: listBillingItemModify}));
            }

            await Promise.all(itemPromises);

            if (dataBilling) {
                await UpdateBilling(dataBilling.id, billing);
                toast.success("Facture modifiée avec succès.", "Facture");
            } else {
                await AddBilling(billing);
                toast.success("Facture ajoutée avec succès.", "Facture");
            }
            onFinished();
        } catch (err) {
            onError(err);
        }
    };

    return (
        <form className="fcaForm" onSubmit={(e) => e.preventDefault()}>
            {loading && <FCSpinner size="small"/>}
            {error && <FCAlertMsgErrorAPI error={error}/>}

            {/* Informations Client & Contrat */}
            <div className="fcaSection">
                <div className="fcaSectionLabel">Informations client & contrat</div>
                <div className={`fcaField${fieldErrors.inputBusiness ? " fcaField--error" : ""}`}>
                    <label>Contrat</label>
                    <FCSelect
                        options={optionsBusiness}
                        value={inputBusiness}
                        onChange={(v) => setInputBusiness(Number(v))}
                        placeholder="Sélectionner un contrat"
                    />
                    {fieldErrors.inputBusiness && <span className="fcaFieldError">{fieldErrors.inputBusiness}</span>}
                </div>
            </div>

            {/* Détails du document */}
            <div className="fcaSection">
                <div className="fcaSectionLabel">Détails du document</div>
                <div className="fcaRow">
                    <div className={`fcaField${fieldErrors.inputNumber ? " fcaField--error" : ""}`}>
                        <label htmlFor="num">Numéro</label>
                        <input id="num" type="text" value={inputNumber}
                               onChange={(e) => setInputNumber(e.target.value)}/>
                        {fieldErrors.inputNumber && <span className="fcaFieldError">{fieldErrors.inputNumber}</span>}
                    </div>
                    <div className="fcaField">
                        <label>Statut</label>
                        <FCSelect
                            options={StatusBilling.ListStatus.map((s) => ({value: s, label: StatusBilling.toString(s)}))}
                            value={inputState}
                            onChange={(v) => setInputState(Number(v))}
                        />
                    </div>
                </div>
                <div className="fcaRow">
                    <div className={`fcaField${fieldErrors.dateBegin ? " fcaField--error" : ""}`}>
                        <FCDatePicker onChangeHandler={setDateBegin} value={dateBegin} id="deb" label="Date de début"/>
                        {fieldErrors.dateBegin && <span className="fcaFieldError">{fieldErrors.dateBegin}</span>}
                    </div>
                    <div className={`fcaField${fieldErrors.dateEnd ? " fcaField--error" : ""}`}>
                        <FCDatePicker onChangeHandler={setDateEnd} value={dateEnd} id="fin" label="Date de fin"/>
                        {fieldErrors.dateEnd && <span className="fcaFieldError">{fieldErrors.dateEnd}</span>}
                    </div>
                </div>
            </div>

            {/* Lignes de prestation */}
            {dataBilling && (
                <div className="fcaSection">
                    <div className="fcaSectionLabel">Lignes de prestation</div>
                    <TableBillingItem dataItemBilling={dataBilling.billingItems} dataBilling={dataBilling}
                                      handlerEvent={handlerEventContact}/>
                </div>
            )}

            <div className="fcaActions">
                <button type="button" className="fcaBtn fcaBtnSecondary" onClick={() => onFinished()}>
                    Annuler
                </button>
                <button type="button" className="fcaBtn fcaBtnPrimary" onClick={handleSubmit}>
                    <span className="material-symbols-outlined">check</span>
                    {dataBilling ? "Enregistrer" : "Valider la facture"}
                </button>
            </div>
        </form>
    );
};

export default FormBilling;
